import type {
  BreakoutsQuery,
  CycleMapQuery,
  HistoryQuery,
  SignalsPaging,
  SteadyQuery,
  TrendQuery,
  VolatileQuery,
} from '@/schemas/signals'

/**
 * Query key factory for Signal Zone: one branch per endpoint, the rule
 * `features/brokers/api/keys.ts` states, so two reads that happen to share a
 * row shape can never hand each other their rows.
 *
 * A session `date` of `null` means "mm-be's latest" and is its own key, on
 * purpose: before 16:45 NPT that is yesterday's session, after it today's,
 * and the two are the same data only until the next run lands.
 *
 * `signalsKeys.all` sweeps every signals read at once (a manual refresh).
 */
export const signalsKeys = {
  all: ['signals'] as const,

  sessions: (limit: number) => [...signalsKeys.all, 'sessions', { limit }] as const,
  dashboard: (date: string | null) => [...signalsKeys.all, 'dashboard', date] as const,
  daily: (date: string | null) => [...signalsKeys.all, 'daily', date] as const,
  live: (run: number | null) => [...signalsKeys.all, 'live', run] as const,
  liveRuns: () => [...signalsKeys.all, 'live-runs'] as const,
  trend: (query: TrendQuery) => [...signalsKeys.all, 'trend', query] as const,
  breakouts: (query: BreakoutsQuery) => [...signalsKeys.all, 'breakouts', query] as const,
  volatile: (query: VolatileQuery) => [...signalsKeys.all, 'volatile', query] as const,
  steady: (query: SteadyQuery) => [...signalsKeys.all, 'steady', query] as const,
  momentum: (paging: SignalsPaging) => [...signalsKeys.all, 'momentum', paging] as const,
  openTrades: () => [...signalsKeys.all, 'open-trades'] as const,
  history: (query: HistoryQuery) => [...signalsKeys.all, 'history', query] as const,
  /** One stock's replayed signals and figures (the Signal page). */
  performance: (symbol: string) => [...signalsKeys.all, 'performance', symbol] as const,
  /** One stock's indicator readings, gauges and pivots (the Technicals page). */
  technicals: (symbol: string) => [...signalsKeys.all, 'technicals', symbol] as const,
  /** One stock's open setup (the chart rail's Glance card and Levels rows). */
  setup: (symbol: string) => [...signalsKeys.all, 'setup', symbol] as const,
  cycleMap: (query: CycleMapQuery) => [...signalsKeys.all, 'cycle-map', query] as const,
  indexSymbols: () => [...signalsKeys.all, 'index-symbols'] as const,
}
